import React, { useEffect, useState } from "react"

import {
    DeepMap,
    FieldError,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormUnregister,
} from "react-hook-form"

import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element"
import { LookupService } from "@front-shared/services/lookup.service"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { LookupHelper } from "../../../helpers/lookup.helper"

export const PayerMappingsPayerInputComponent: React.FunctionComponent<
    IPayerMappingsPayerInputComponentProps
> = props => {
    const [resolvedPayer, setResolvedPayer] = useState<IPayer>()

    useEffect(() => {
        setResolvedPayer(undefined)

        const payerId = props.defaultPayerId
        if (!payerId) {
            return
        }

        let isCancelled = false

        LookupService.lookupPayers(payerId).then(items => {
            if (isCancelled) {
                return
            }

            const match = items.find(item => item.payerId === payerId)
            if (!match) {
                return
            }

            setResolvedPayer(match)
            props.reactHookFormSet?.("payer", match)
        })

        return () => {
            isCancelled = true
        }
    }, [props.defaultPayerId, props.reactHookFormSet])

    return (
        <AutoCompleteElement
            className={props.className}
            name="payer"
            label="Nirvana Payer ID"
            placeholder=""
            withRequiredStar={true}
            searchOnFocus={true}
            shouldValidate={true}
            dataFetcher={(search?: string) =>
                LookupService.lookupPayers(search)
            }
            ignoreAutoSelectionOnBlur={true}
            itemRenderer={LookupHelper.getRenderedPayer}
            defaultValue={
                resolvedPayer
                    ? LookupHelper.getRenderedPayer(resolvedPayer).displayValue
                    : props.defaultPayerId
            }
            debounceMilliseconds={250}
            reactHookFormRegister={props.reactHookFormRegister}
            reactHookFormUnregister={props.reactHookFormUnregister}
            reactHookFormErrors={props.reactHookFormErrors}
            reactHookFormSet={props.reactHookFormSet}
            validations={{
                required: FRONT_VALIDATION_CONFIG.required,
            }}
            disabled={props.disabled}
            notFoundTitle="Don’t see your payer?"
            notFoundSubtitle={`
                <a
                    href="mailto:${GENERAL_CONFIG.supportEmail}?subject=Insurance provider not found"
                    target="_blank"
                    rel="noreferrer"
                >
                    Contact Support
                </a>
            `}
        />
    )
}

interface IPayerMappingsPayerInputComponentProps {
    className?: string
    disabled?: boolean
    defaultPayerId?: string
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormUnregister?: UseFormUnregister<FieldValues>
    reactHookFormSet?: UseFormSetValue<FieldValues>
    reactHookFormErrors?: DeepMap<FieldValues, FieldError>
}
